import { createSlice, AnyAction } from "@reduxjs/toolkit";
import {
  convertISODateTime,
  convertDefaultTimeZoneStringToISO,
  roundToInterval,
} from "@utils/time-helpers";
import moment from "moment-timezone";
import type { BannerDetails, ImageDetail } from "../types";

const initialState: BannerDetails = {
  saveEnabled: true,
  banner_id: "",
  title: "",
  platform_id: null,
  inputISOStartDate: null,
  inputISOStopDate: null,
  formerPosition: {},
  position: {},
  selectedPositions: [],
  script_type: "",
  script_value: "",
  segment_ids: [],

  //For error checking
  original_start_date: null,
  original_stop_date: null,

  // segments
  segmentationEnabled: false,
  segments: [],

  // supplemental
  supplementalEnabled: true,
  supplemental_data: {},
  supplementalDataLoaded: false,

  imageDetails: {},

  selectedImage: {
    url: "",
    name: "",
    fileSize: "",
    height: null,
    width: null,
    base64: "",
  },

  showUploadImagePanel: false,
  showDropZoneNew: false,
  validInputs: false,
  errors: {},
};

const bannerNewViewSlice = createSlice({
  name: "bannerNewView",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase("LOAD_BANNER", (state, action: AnyAction) => {
        Object.assign(state, action.payload);
        state.original_start_date = (action.payload as any).start_date || null;
        state.original_stop_date = (action.payload as any).stop_date || null;
        state.supplementalDataLoaded = true;
      })
      .addCase("LOAD_STATUS_DETAIL", (state, action: AnyAction) => {
        state.banner_id = action.idTarget;
        (state as any).is_live = action.itemStatus;
      })
      .addCase("RESET_FORM_FIELDS", () => initialState)
      .addCase("SET_TITLE", (state, action: AnyAction) => {
        state.title = action.title;
      })
      .addCase("SET_POSITION", (state, action: AnyAction) => {
        const newImageDetails: Record<string, ImageDetail> = {};
        Object.keys(state.imageDetails).forEach((key) => {
          newImageDetails[key] = {
            ...state.imageDetails[key],
            fitsPosition:
              (state.imageDetails[key].width ?? 0) <=
                (action.position.width ?? 0) &&
              (state.imageDetails[key].height ?? 0) <=
                (action.position.height ?? 0),
          };
        });
        state.position = action.position;
        state.platform_id = action.position.platformId
          ? action.position.platformId
          : state.platform_id;
        state.imageDetails = newImageDetails;
        state.formerPosition = state.position;
      })
      .addCase("SET_SELECTED_POSITIONS", (state, action: AnyAction) => {
        state.selectedPositions = action.positions;
      })
      .addCase("SET_PLATFORM", (state, action: AnyAction) => {
        state.platform_id = action.platform;
      })
      .addCase("SET_NEW_DATE", (state, action: AnyAction) => {
        let timeString: string,
          combinedString: string,
          newISODateTime: string | null = null,
          dateToSet: string | null;
        if (action.dateString) {
          dateToSet =
            action.whatToSet === "startDate"
              ? state.inputISOStartDate
              : state.inputISOStopDate;
          timeString = dateToSet
            ? dateToSet
            : moment.tz("America/New_York").toISOString();
          timeString = (convertISODateTime(
            roundToInterval(timeString, 1)
          ) as any)?.toMomentWithConsoleConfigTimeZone?.format("h:mmA");
          combinedString = action.dateString + timeString;
          newISODateTime = convertDefaultTimeZoneStringToISO(
            combinedString
          ) as unknown as string;
        }
        if (action.whatToSet === "startDate") {
          state.inputISOStartDate = newISODateTime;
        } else {
          state.inputISOStopDate = newISODateTime;
        }
      })
      .addCase("SET_NEW_TIME", (state, action: AnyAction) => {
        const dateToSet =
          action.whatToSet === "startTime"
            ? state.inputISOStartDate
            : state.inputISOStopDate;
        const dateString = (convertISODateTime(
          dateToSet
        ) as any)?.toMomentWithConsoleConfigTimeZone?.format("MM/DD/YYYY");
        const combinedString = dateString + action.timeString;
        const newISODateTime = convertDefaultTimeZoneStringToISO(
          combinedString
        ) as unknown as string;
        if (action.whatToSet === "startTime") {
          state.inputISOStartDate = newISODateTime;
        } else {
          state.inputISOStopDate = newISODateTime;
        }
      })
      .addCase("SET_ACTION_TYPE", (state, action: AnyAction) => {
        state.script_type = action.script_type;
      })
      .addCase("SET_ACTION_VALUE", (state, action: AnyAction) => {
        state.script_value = action.script_value;
      })
      .addCase("SET_USER_SEGMENT", (state, action: AnyAction) => {
        let modifiedSegments: any[];
        let modifiedSegmentIds: Array<string | number>;
        if ((action.segment_id as any).length === 0) {
          modifiedSegments = [];
          modifiedSegmentIds = [];
        } else {
          modifiedSegments = state.segments.map((segment: any) => ({
            ...segment,
            checked:
              segment.segment_id === action.segment_id
                ? !segment.checked
                : segment.checked,
          }));
          modifiedSegmentIds = (state.segment_ids as any[]).includes(
            action.segment_id
          )
            ? (state.segment_ids as any[]).filter(
                (segmentId: any) => segmentId !== action.segment_id
              )
            : (state.segment_ids as any[]).concat(action.segment_id);
        }
        state.segments = modifiedSegments;
        state.segment_ids = modifiedSegmentIds as any;
      })
      .addCase("SET_SUPPLEMENTAL_DATA", (state, action: AnyAction) => {
        const formattedSupplementalData = (action.supplementalData as any[]).reduce(
          (accumulator: Record<string, string>, currentValue: any) =>
            currentValue.rowName
              ? { ...accumulator, [currentValue.rowName]: currentValue.rowValue }
              : accumulator,
          {}
        );
        state.supplemental_data = formattedSupplementalData;
      })
      .addCase("STORE_IMAGE_DETAILS", (state, action: AnyAction) => {
        (state.imageDetails as any)[action.imageDetails.image] = {
          height: action.imageDetails.height,
          width: action.imageDetails.width,
          fitsPosition: action.imageDetails.fitsPosition,
        };
      })
      .addCase("SET_SHOW_UPLOAD_PANEL", (state, action: AnyAction) => {
        state.showUploadImagePanel = action.show;
      })
      .addCase("SET_SHOW_DROP_ZONE", (state, action: AnyAction) => {
        state.showDropZoneNew = action.show;
        state.showUploadImagePanel = false;
      })
      .addCase("CLEAR_SELECTED_IMAGE", (state) => {
        state.validInputs = false;
        state.selectedImage = { ...initialState.selectedImage };
      })
      .addCase("RESTORE_POSITION", (state) => {
        state.position = state.formerPosition;
      })
      .addCase("CLEAR_POSITION", (state) => {
        state.position = initialState.position;
        state.formerPosition = initialState.position;
      })
      .addCase("SELECT_IMAGE", (state, action: AnyAction) => {
        state.showUploadImagePanel = false;
        state.selectedImage.url = action.img;
        state.selectedImage.fileSize = action.fileSize || "";
        if ("pixelSize" in action && action.pixelSize) {
          const { height } = action.pixelSize;
          (state as any).expectedPixelSize = height || "";
        }
      })
      .addCase("SET_ACTUAL_HEIGHT_WIDTH", (state, action: AnyAction) => {
        state.selectedImage.height = action.imageDetails.height;
        state.selectedImage.width = action.imageDetails.width;
      })
      .addCase("DROP_FILE", (state, action: AnyAction) => {
        const file = action.file[0];
        state.showDropZoneNew = false;
        state.selectedImage.url = file.preview;
        state.selectedImage.name = file.name;
        state.selectedImage.base64 = action.base64;
        state.selectedImage.fileSize = (file.size / 1024).toFixed(2);
      })
      .addCase("ENABLE_POST", (state, action: AnyAction) => {
        state.validInputs = !action.hasErrors;
      })
      .addCase("SET_ERROR", (state, action: AnyAction) => {
        if ((action.error as any).errorName) {
          (state.errors as any)[(action.error as any).errorName] = (
            action.error as any
          ).errorPresent;
        } else {
          Object.assign(state.errors, action.error);
        }
      });
  },
});

export const selectBannerNewView = (state: {
  bannerNewView: BannerDetails;
}): BannerDetails => state.bannerNewView;

export default bannerNewViewSlice.reducer;
